// =====================================================
// App entry — providers + router.
//
// Phase-14 R3 moved the route table + Nav/ErrorBoundary chrome into
// router.tsx (see useGo / useCurrentRoute / Layout there). This file
// is now just the provider stack wrapping <AppRouter />.
//
// Phase-14.5 C3: dropped @ts-nocheck.
// =====================================================

import React from "react";
import { RoleProvider } from "./role";
import { AuthProvider } from "./auth/AuthContext";
import { twoFactorGate } from "./auth/two-factor-gate.js";
import { TermProvider } from "./term-context";
import { ThemeProvider } from "./ui";
import { AppRouter } from "./router";

// S-2/S-3 — the forced-2FA overlay pulls in qrcode.react, so keep it OUT of the
// eager main bundle (S-19 bundle-budget lesson). A tiny eager wrapper watches the
// gate signal and lazy-loads the heavy component ONLY when the gate is actually
// raised (enforcement ON + a privileged un-enrolled user) — zero cost otherwise.
const TwoFactorRequiredGate = React.lazy(() =>
  import("./auth/TwoFactorRequiredGate").then((m) => ({ default: m.TwoFactorRequiredGate })),
);

const TwoFactorGateMount: React.FC = () => {
  const [required, setRequired] = React.useState<boolean>(() => twoFactorGate.get());
  React.useEffect(() => {
    const unsub = twoFactorGate.subscribe(setRequired);
    return () => {
      unsub();
    };
  }, []);
  if (!required) return null;
  return (
    <React.Suspense fallback={null}>
      <TwoFactorRequiredGate />
    </React.Suspense>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AuthProvider>
      <RoleProvider>
        {/* R-SIS-3d — term context for the navbar switcher + term-aware views. */}
        <TermProvider>
          <AppRouter />
          {/* S-2/S-3 — forced 2FA-enrolment overlay (inert by default; lazy). */}
          <TwoFactorGateMount />
        </TermProvider>
      </RoleProvider>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
